import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useOptimization } from '../../context/OptimizationContext';

interface OptimizationProgressProps {
  compact?: boolean;
}

const OptimizationProgress: React.FC<OptimizationProgressProps> = ({ compact = false }) => {
  const { isRunning, progress, currentAlgorithm, currentIteration, totalIterations, currentRun, totalRuns, completedAlgorithms, results } = useOptimization();

  if (!isRunning && !results) {
    return null;
  }

  if (compact) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {isRunning ? `Running ${currentAlgorithm || 'optimization'}...` : 'Optimization Complete'}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        backgroundColor: '#e8e8e8', // Silver/gray background
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
          Optimization Progress
        </Typography>
        {!isRunning && (
          <Chip label="Complete" color="success" size="small" />
        )}
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Overall Optimization Progress
          </Typography>
          <Typography variant="body2" color="primary.main" fontWeight={600}>
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 16,
            borderRadius: 8,
            backgroundColor: '#c0c0c0', // Silver/gray background for progress bar
            '& .MuiLinearProgress-bar': {
              borderRadius: 8,
              backgroundColor: '#1976d2', // Primary blue for progress
            },
          }}
        />
      </Box>

      {/* Status Information */}
      <Stack spacing={2}>
        {isRunning && (
          <>
            {/* Run Counter - Prominent Display */}
            {totalRuns > 1 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Run Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700, minWidth: 100 }}>
                    Run {currentRun} / {totalRuns}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(currentRun / totalRuns) * 100}
                    sx={{
                      flexGrow: 1,
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: '#c0c0c0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 6,
                        backgroundColor: '#0288d1', // Info color for runs
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>
                    {Math.round((currentRun / totalRuns) * 100)}%
                  </Typography>
                </Box>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Current Activity
              </Typography>
              {currentAlgorithm && (
                <Box sx={{ mb: 1 }}>
                  <Chip
                    label={`Algorithm: ${currentAlgorithm}`}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600, mr: 1 }}
                  />
                  {currentIteration > 0 && totalIterations > 0 && (
                    <Chip
                      label={`Iteration: ${currentIteration}/${totalIterations}`}
                      color="secondary"
                      size="small"
                      sx={{ fontWeight: 500, mr: 1 }}
                    />
                  )}
                </Box>
              )}
            </Box>
            {currentIteration > 0 && totalIterations > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Algorithm Iteration Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 80 }}>
                    {currentIteration} / {totalIterations}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(currentIteration / totalIterations) * 100}
                    sx={{
                      flexGrow: 1,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#c0c0c0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 45 }}>
                    {Math.round((currentIteration / totalIterations) * 100)}%
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Algorithms Status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            {['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].map((alg) => {
              // Extract algorithm name from currentAlgorithm (handles "Complete Run: X tasks - Algorithm" format)
              const extractAlgorithmName = (algo: string): string => {
                if (algo.includes(' - ')) {
                  return algo.split(' - ').pop() || algo;
                }
                return algo;
              };
              
              const currentAlgName = extractAlgorithmName(currentAlgorithm || '');
              const algIndex = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'].indexOf(alg);
              const baseProgress = (algIndex / 6) * 100;
              const algProgress = progress / 6;
              
              // For complete run: use completedAlgorithms set
              // For regular optimization: use progress-based calculation
              const isCompleteRun = totalRuns > 1 || currentAlgorithm.includes('Complete Run');
              const isCompleted = isCompleteRun 
                ? completedAlgorithms.has(alg)
                : progress > baseProgress + algProgress;
              
              const isCurrent = currentAlgName === alg && isRunning;
              
              return (
                <Chip
                  key={alg}
                  label={alg}
                  size="small"
                  color={isCompleted ? 'success' : isCurrent ? 'primary' : 'default'}
                  variant={isCompleted ? 'filled' : isCurrent ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: isCurrent ? 600 : 400,
                    opacity: progress < baseProgress ? 0.5 : 1,
                  }}
                />
              );
            })}
          </Box>
          
          {/* Show best values under Algorithms Status when results are available */}
          {results && (() => {
            const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;
            const algorithmNames = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'];
            
            // Find best fitness (highest)
            const fitnessValues = algorithmKeys.map(key => results[key].fitness);
            const maxFitness = Math.max(...fitnessValues);
            const bestFitnessIndex = fitnessValues.indexOf(maxFitness);
            const bestFitnessAlgorithm = algorithmNames[bestFitnessIndex];
            
            // Find best energy (lowest)
            const energyValues = algorithmKeys.map(key => results[key].energy);
            const minEnergy = Math.min(...energyValues);
            const bestEnergyIndex = energyValues.indexOf(minEnergy);
            const bestEnergyAlgorithm = algorithmNames[bestEnergyIndex];
            
            // Find best cost (lowest)
            const costValues = algorithmKeys.map(key => results[key].cost);
            const minCost = Math.min(...costValues);
            const bestCostIndex = costValues.indexOf(minCost);
            const bestCostAlgorithm = algorithmNames[bestCostIndex];
            
            // Find best response time (lowest)
            const responseTimeValues = algorithmKeys.map(key => results[key].responseTime);
            const minResponseTime = Math.min(...responseTimeValues);
            const bestResponseTimeIndex = responseTimeValues.indexOf(minResponseTime);
            const bestResponseTimeAlgorithm = algorithmNames[bestResponseTimeIndex];
            
            return (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
                  Best Results
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Best Fitness:
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 700 }}>
                      {maxFitness.toFixed(4)} ({bestFitnessAlgorithm})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Best Energy:
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 700 }}>
                      {minEnergy.toFixed(2)} J ({bestEnergyAlgorithm})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Best Cost:
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 700 }}>
                      {minCost.toFixed(4)} ({bestCostAlgorithm})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Best Response Time:
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 700 }}>
                      {minResponseTime.toFixed(2)} ms ({bestResponseTimeAlgorithm})
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>

        {results && !isRunning && (() => {
          const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;
          const algorithmNames = ['GA', 'Cultural', 'SFLA', 'SFLAGA', 'PSO', 'GAPSO'];
          
          // Find best fitness (highest)
          const fitnessValues = algorithmKeys.map(key => results[key].fitness);
          const maxFitness = Math.max(...fitnessValues);
          const bestFitnessIndex = fitnessValues.indexOf(maxFitness);
          const bestFitnessAlgorithm = algorithmNames[bestFitnessIndex];
          
          // Find best energy (lowest)
          const energyValues = algorithmKeys.map(key => results[key].energy);
          const minEnergy = Math.min(...energyValues);
          const bestEnergyIndex = energyValues.indexOf(minEnergy);
          const bestEnergyAlgorithm = algorithmNames[bestEnergyIndex];
          
          // Find best cost (lowest)
          const costValues = algorithmKeys.map(key => results[key].cost);
          const minCost = Math.min(...costValues);
          const bestCostIndex = costValues.indexOf(minCost);
          const bestCostAlgorithm = algorithmNames[bestCostIndex];
          
          // Find best response time (lowest)
          const responseTimeValues = algorithmKeys.map(key => results[key].responseTime);
          const minResponseTime = Math.min(...responseTimeValues);
          const bestResponseTimeIndex = responseTimeValues.indexOf(minResponseTime);
          const bestResponseTimeAlgorithm = algorithmNames[bestResponseTimeIndex];
          
          // Find best execution time (lowest) - if available
          const executionTimeValues = algorithmKeys.map(key => results[key].executionTime || Infinity);
          const minExecutionTime = Math.min(...executionTimeValues.filter(t => t !== Infinity));
          const bestExecutionTimeIndex = executionTimeValues.indexOf(minExecutionTime);
          const bestExecutionTimeAlgorithm = algorithmNames[bestExecutionTimeIndex];
          
          return (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
                Best Results Summary
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                {/* Best Fitness */}
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'auto' } }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Best Fitness Score
                  </Typography>
                  <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                    {maxFitness.toFixed(2)} <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5 }}>({bestFitnessAlgorithm})</Typography>
                  </Typography>
                </Box>
                
                {/* Best Energy */}
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'auto' } }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Best Energy (Lowest)
                  </Typography>
                  <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                    {minEnergy.toFixed(2)} J <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5 }}>({bestEnergyAlgorithm})</Typography>
                  </Typography>
                </Box>
                
                {/* Best Cost */}
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'auto' } }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Best Execution Cost (Lowest)
                  </Typography>
                  <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                    {minCost.toFixed(2)} <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5 }}>({bestCostAlgorithm})</Typography>
                  </Typography>
                </Box>
                
                {/* Best Response Time */}
                <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'auto' } }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Best Response Time (Lowest)
                  </Typography>
                  <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                    {minResponseTime.toFixed(2)} ms <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5 }}>({bestResponseTimeAlgorithm})</Typography>
                  </Typography>
                </Box>
                
                {/* Best Execution Time */}
                {minExecutionTime !== Infinity && (
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'auto' } }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Best Execution Time (Fastest)
                    </Typography>
                    <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                      {minExecutionTime.toFixed(2)} ms <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600, ml: 0.5 }}>({bestExecutionTimeAlgorithm})</Typography>
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })()}
      </Stack>
    </Paper>
  );
};

export default OptimizationProgress;

